/**
 * Role Access Management Service
 * Handles all API calls related to role access management
 * Uses fetchWithAuth for JWT + refresh token on 401
 */

import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Service class for Role Access Management API calls
 */
class RoleAccessManagementService {
  /**
   * Get all engineers
   * @returns {Promise<Array>} List of engineers
   */
  static async getEngineers() {
    try {
      const roleId = 3; // ENGINEER role ID
      const apiUrl = `${API_BASE_URL}/api/v1/users/users-detail?roleId=${roleId}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch engineers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Map API response to UI expected format
      // API returns: { message, usersDetail: [{ userId, roleId, username, mobileNo, emailId, status, createdAt, updatedAt }] }
      // UI expects: [{ id, username, mobileNo, emailId, status, createdAt, updatedAt }]
      if (data.usersDetail && Array.isArray(data.usersDetail)) {
        return data.usersDetail.map(user => ({
          id: user.userId,
          username: user.username || '',
          mobileNo: user.mobileNo || '',
          emailId: user.emailId || '',
          status: user.status || 'ACTIVE',
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching engineers:', error);
      throw error;
    }
  }

  /**
   * Get all customers with company details (ADMIN / SUPERADMIN)
   * @returns {Promise<Array>} List of customers
   */
  static async getCustomers() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/users/customers-detail`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to fetch customers: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // API returns: { message, usersDetail: [{ userId, roleId, username, mobileNo, emailId, status, createdAt, updatedAt, companyId, companyName }] }
      // UI expects: [{ id, username, mobileNo, emailId, company, companyId, status, createdAt, updatedAt }]
      if (data.usersDetail && Array.isArray(data.usersDetail)) {
        return data.usersDetail.map((user) => ({
          id: user.userId,
          username: user.username || '',
          mobileNo: user.mobileNo || '',
          emailId: user.emailId || '',
          companyId: user.companyId ?? null,
          company: user.companyName || '',
          status: user.status || 'ACTIVE',
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  /**
   * Get pending customer access requests (PENDING, active, newest first)
   * @returns {Promise<Array>} List of customer requests
   */
  static async getCustomerRequests() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/users/pending-customer-access-requests`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch customer access requests: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // API returns: { totalCount, requests: [{ requestId, username, mobileNo, emailId, companyName }] }
      // UI expects: [{ id, requestId, username, mobileNo, emailId, company }]
      if (data.requests && Array.isArray(data.requests)) {
        return data.requests.map((request) => ({
          id: request.requestId,
          requestId: request.requestId,
          username: request.username || '',
          mobileNo: request.mobileNo || '',
          emailId: request.emailId || '',
          company: request.companyName || '',
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching customer requests:', error);
      throw error;
    }
  }

  /**
   * Get all admins
   * @returns {Promise<Array>} List of admins
   */
  static async getAdmins() {
    try {
      const roleId = 4; // ADMIN role ID
      const apiUrl = `${API_BASE_URL}/api/v1/users/users-detail?roleId=${roleId}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch admins: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Map API response to UI expected format
      // API returns: { message, usersDetail: [{ userId, roleId, username, mobileNo, emailId, status, createdAt, updatedAt }] }
      // UI expects: [{ id, username, mobileNo, emailId, status, createdAt, updatedAt }]
      if (data.usersDetail && Array.isArray(data.usersDetail)) {
        return data.usersDetail.map(user => ({
          id: user.userId,
          username: user.username || '',
          mobileNo: user.mobileNo || '',
          emailId: user.emailId || '',
          status: user.status || 'ACTIVE',
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching admins:', error);
      throw error;
    }
  }

  /**
   * Add a new user (engineer/admin)
   * @param {Object} userData - User data (username, mobileNo, emailId)
   * @param {string} roleType - Type of user ('engineers' or 'admins')
   * @returns {Promise<Object>} Created user data
   */
  static async addUser(userData, roleType) {
    try {
      // Determine isAdmin flag based on roleType
      const isAdmin = roleType === 'admins';
      
      // Prepare request body
      const requestBody = {
        username: userData.username,
        mobileNo: userData.mobileNo,
        emailId: userData.emailId,
        isAdmin: isAdmin,
      };

      // API endpoint - same for both Admin and Engineer, only isAdmin flag differs
      const apiUrl = `${API_BASE_URL}/api/v1/users/add-admin-engineer`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to add ${roleType === 'admins' ? 'admin' : 'engineer'}: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      
      // Map API response to UI expected format
      // API returns: { userId, userUid, username, mobileNo, emailId, status, createdAt, updatedAt }
      // UI expects: { id, username, mobileNo, emailId, status, createdAt, updatedAt }
      return {
        id: responseData.userId,
        userUid: responseData.userUid,
        username: responseData.username || '',
        mobileNo: responseData.mobileNo || '',
        emailId: responseData.emailId || '',
        status: responseData.status || 'ACTIVE',
        createdAt: responseData.createdAt,
        updatedAt: responseData.updatedAt,
      };
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  }

  /**
   * Update a user
   * @param {number} userId - User ID
   * @param {Object} userData - Updated user data
   * @param {string} roleType - Type of user
   * @returns {Promise<Object>} Updated user data
   */
  static async updateUser(userId, userData, roleType) {
    try {
      // TODO: Replace with actual API endpoint
      // const endpoint = roleType === 'engineers' ? '/api/engineers' : roleType === 'customers' ? '/api/customers' : '/api/admins';
      // const response = await fetch(`${API_BASE_URL}${endpoint}/${userId}`, {
      //   method: 'PUT',
      //   headers: getHeaders(),
      //   body: JSON.stringify(userData),
      // });
      // if (!response.ok) throw new Error('Failed to update user');
      // return await response.json();

      console.log('Update user:', userId, userData, roleType);
      // Mock response - will be replaced with actual API call
      return { id: userId, ...userData };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Remove customer access (soft-delete CUSTOMER role mapping)
   * @param {number} userId - User ID from customers-detail
   * @returns {Promise<Object>} Response with msg
   */
  static async removeCustomerAccess(userId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/users/remove-customer-access?userId=${encodeURIComponent(userId)}`;

      const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          errorData.msg ||
          `Failed to remove customer access: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing customer access:', error);
      throw error;
    }
  }

  /**
   * Delete a user (admin/engineer)
   * @param {number} userId - User ID
   * @param {string} roleType - Type of user ('engineers' or 'admins')
   * @returns {Promise<Object>} Response message
   */
  static async deleteUser(userId, roleType) {
    try {
      // Determine isAdmin flag based on roleType
      const isAdmin = roleType === 'admins';
      
      // API endpoint - DELETE with query parameters
      const apiUrl = `${API_BASE_URL}/api/v1/users/remove-admin-engineer?userId=${userId}&isAdmin=${isAdmin}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to remove ${roleType === 'admins' ? 'admin' : 'engineer'} access: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Approve a customer access request
   * @param {number} requestId - Request ID
   * @returns {Promise<Object>} Response with message
   */
  static async approveCustomerRequest(requestId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/users/approve-customer-access-request?requestId=${requestId}`;

      const response = await fetchWithAuth(apiUrl, { method: 'POST' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to approve customer access request: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  }

  /**
   * Reject a customer access request
   * @param {number} requestId - Request ID
   * @returns {Promise<Object>} Response with message
   */
  static async rejectCustomerRequest(requestId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/users/reject-customer-access-request?requestId=${requestId}`;

      const response = await fetchWithAuth(apiUrl, { method: 'POST' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to reject customer access request: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error rejecting request:', error);
      throw error;
    }
  }
}

export default RoleAccessManagementService;

